import { Expansion } from '@/lib/types/common/Expansion';
import React, { useEffect } from 'react'
import Image from 'next/image';
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import TextFFXIV from '@/app/components/TextFFXIV';
import { EXPANSION_CONFIG } from '@/lib/utils/gameConfigUtils';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';

const GameModeConfigExpansions = () => {
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const { t } = useTranslation();
    const { play } = useAudio();

    const isGameMaster = room?.sessionId === gameState?.gameMasterId;
    const selectedExpansions = Array.from(gameState?.selectedExpansions || []) as Expansion[];

    // Save selected expansions to localStorage whenever they change (game master only)
    useEffect(() => {
        if (isGameMaster && typeof window !== 'undefined' && selectedExpansions.length > 0) {
            localStorage.setItem('eorguessr_selected_expansions', JSON.stringify(selectedExpansions));
        }
    }, [selectedExpansions, isGameMaster]);

    const expansions = EXPANSION_CONFIG.map(config => ({
        name: t(config.name),
        expansion: config.expansion,
        icon: config.icon
    }));

    if (!room || !gameState) return null;

    return (
        <div
            className={`flex gap-3 flex-col md:flex-row items-center ${!isGameMaster ? 'opacity-50' : ''}`}
        >
            <TextFFXIV text={t("lobby.expansions")} className="w-[75px]" />
            <div
                className="flex gap-2.5"
            >
                {expansions.map(exp => {
                    const isSelected = selectedExpansions.includes(exp.expansion);
                    return (
                        <div
                            key={exp.expansion}
                            className={`relative group flex gap-2 items-center z-10 ${isGameMaster ? 'cursor-pointer' : 'cursor-not-allowed'} ${isSelected ? '' : 'opacity-60'}`}
                            onMouseEnter={() => isGameMaster && play(AUDIO.HOVER)}
                            onClick={() => {
                                if (!isGameMaster) {
                                    showPopup({
                                        type: 'error',
                                        message: t('lobby.onlyGameMasterCan'),
                                    });
                                    return;
                                }
                                play(AUDIO.CLICK);
                                room.send("toggleExpansion", { expansion: exp.expansion });
                            }}
                        >
                            <TooltipFFXIV
                                showInMobile={false}
                            >
                                {exp.name}
                            </TooltipFFXIV>
                            <div className="relative w-8 h-8">
                                {isSelected && (
                                    <div className="left-[-2px] top-[-2px] rounded-md absolute w-[36px] h-[36px] z-40 border-2 border-[#CDCCA8] shadow-[inset_0px_0px_2px_2px_rgba(0,0,0,0.6),0px_0px_2px_2px_rgba(0,0,0,0.5)]"></div>
                                )}
                                <Image
                                    src={exp.icon}
                                    alt={exp.name}
                                    width={32}
                                    height={32}
                                    className="w-8 h-8 shadow shadow-black/50 rounded"
                                />
                                <div className="absolute top-0 left-0 w-full h-full rounded bg-white group-hover:opacity-10 opacity-0 duration-100"></div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default GameModeConfigExpansions